
import './Stamp.css';
import React, { useState, useEffect, useRef } from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { CircularProgress } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const API_BASE_URL = 'https://api.waraq.co/waraq/api/v1';

const getAuthHeaders = () => {
  const token = localStorage.getItem("jwtToken");
  return { Authorization: `${token}` };
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch {
    return dateString;
  }
};

const getFileName = (url) => url?.split('.com/')[1] || 'document';


const styles = {
  container: {
    padding: '20px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  taskRow: {
    cursor: "pointer",
    margin: "30px 0px",
    padding: "10px 30px",
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    borderRadius: "12px",
    border: "1px solid #000",
  },
  taskField: {
    flex: '1 1 50px',
    minWidth: '50px'
  },
  fieldLabel: {
    fontSize: '14px',
    fontWeight: '500'
  },
  statusBadge: {
    backgroundColor: '#FFCD50',
    padding: "6px 16px",
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    color: "#000"
  },
  link: {
    color: "blue",
    textDecoration: "underline",
    cursor: "pointer"
  },
  nextButton: {
    marginTop: '10px',
    fontSize: '14px',
    backgroundColor: "transparent",
    padding: "5px 25px",
    border: "1px solid #000",
    cursor: "pointer",
    borderRadius: "3px",
  },
  popupOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  },
  popupContent: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxWidth: '400px',
    width: '90%'
  },
  popupTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    textAlign: 'center',
    color: '#333'
  },
  popupButtons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center'
  },
  cancelButton: {
    padding: '8px 20px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  confirmButton: {
    padding: '8px 20px',
    backgroundColor: '#3F302E',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666'
  },
  flexBetween: {
    display: 'flex',
    alignItems: "center",
    justifyContent: 'space-between'
  }
};

const ClientDropdown = ({ clients, selected, setSelected, open, setOpen }) => (
  <div style={{ width: "200px", position: "relative" }}>
    <div
      onClick={() => setOpen(!open)}
      style={{
        background: "#f5f3f0",
        borderRadius: "10px",
        cursor: "pointer",
        display: "flex",
        padding: '5px 20px',
        fontSize: '14px',
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>
        {selected ? `${selected.firstName} ${selected.lastName}` : "Client"}
      </span>
      <span>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</span>
    </div>

    {open && (
      <div
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          marginTop: "5px",
          border: "1px solid #ddd",
          borderRadius: "10px",
          background: "#fff",
          boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
          padding: "8px 0",
          zIndex: 1000,
          maxHeight: "300px",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            padding: "8px 12px",
            cursor: "pointer",
            color: "gray",
            fontSize: "14px",
            borderBottom: "1px solid #eee",
          }}
          onClick={() => {
            setSelected("");
            setOpen(false);
          }}
        >
          Clear Selection
        </div>

        {clients.map((option, i) => (
          <div
            key={option.id}
            style={{
              fontWeight: '400',
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "12px",
              backgroundColor: selected?.id === option.id ? "#f0f0f0" : "transparent",
            }}
            onClick={() => {
              setSelected(option);
              setOpen(false);
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#f5f5f5"}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = selected?.id === option.id ? "#f0f0f0" : "transparent";
            }}
          >
            {option.firstName} {option.lastName}
          </div>
        ))}
      </div>
    )}
  </div>
);

const TaskField = ({ label, value }) => (
  <div style={styles.taskField}>
    <strong style={styles.fieldLabel}>{label}</strong>
    <br />
    {value || "N/A"}
  </div>
);

const DocumentTable = ({
  documents,
  handleDownload,
  handleIconClick,
  handleFileChange,
  handleDeleteClick,
  fileInputRefs,
  uploadingRows,
  selectedFiles
}) => (
  <table className="styled-table" style={{ marginTop: "10px", width: "100%" }}>
    <thead className="styled-thead">
      <tr>
        <th style={styles.fieldLabel}>Document (PDF)</th>
        <th style={styles.fieldLabel}>Template (Word)</th>
        <th style={styles.fieldLabel}>Translation (Word)</th>
        <th style={styles.fieldLabel}>Delivery (Word)</th>
        <th style={styles.fieldLabel}>Created At</th>
      </tr>
    </thead>
    <tbody className="styled-tbody">
      {documents.map((row) => (
        <tr className="styled-row" key={row.id}>
          <td className="styled-cell">
            <a
              onClick={() => handleDownload(row.documentId, row.document)}
              style={styles.link}
            >
              {row?.document.split('.com/')[1]?.length > 25
                ? row?.document.split('.com/')[1].slice(0, 25) + '...'
                : row?.document.split('.com/')[1]}

            </a>
          </td>

          <td className="styled-cell">
            {row.templateUrl ? (
              <a
                onClick={() => handleDownload(row.templateId, row.templateUrl)}
                style={styles.link}
              >
                {row?.templateUrl.split('.com/')[1]?.length > 25
                  ? row?.templateUrl.split('.com/')[1].slice(0, 25) + '...'
                  : row?.templateUrl.split('.com/')[1]}

              </a>
            ) : (
              "No template uploaded"
            )}
          </td>

          <td className="styled-cell">
            {row.translatedDocumentUrl ? (
              <a
                onClick={() => handleDownload(row.translatedDocumentId, row.translatedDocumentUrl)}
                style={styles.link}
              >
                {row?.translatedDocumentUrl.split('.com/')[1]?.length > 25
                  ? row?.translatedDocumentUrl.split('.com/')[1].slice(0, 25) + '...'
                  : row?.translatedDocumentUrl.split('.com/')[1]}

              </a>
            ) : (
              !selectedFiles[row.id] && (
                <FileUploadIcon
                  style={{ fontSize: "16px", cursor: "pointer" }}
                  onClick={() => handleIconClick(row.id)}
                />
              )
            )}
          </td>

          <td className="styled-cell">
            <input
              type="file"
              ref={(el) => (fileInputRefs.current[row.id] = el)}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, row.id)}
              accept="*/*"
            />

            {uploadingRows[row.id] ? (
              <CircularProgress size={16} style={{ color: "#1976d2" }} />
            ) : row.finalTranslatedDocumentUrl ? (
              <div style={styles.flexBetween}>
                <a
                  onClick={() =>
                    handleDownload(row.finalTranslatedDocumentId, row.finalTranslatedDocumentUrl)
                  }
                  style={styles.link}
                >
                  {row?.finalTranslatedDocumentUrl.split(".com/")[1]?.length > 25
                    ? row?.finalTranslatedDocumentUrl.split(".com/")[1].slice(0, 25) + "..."
                    : row?.finalTranslatedDocumentUrl.split(".com/")[1]}
                </a>
                <DeleteIcon
                  onClick={() => handleDeleteClick(row.id)}
                  style={{ fontSize: "18px", color: "red", cursor: "pointer" }}
                />
              </div>
            ) : (
              <FileUploadIcon
                style={{ fontSize: "16px", cursor: "pointer" }}
                onClick={() => handleIconClick(row.id)}
              />
            )}
          </td>


          <td className="styled-cell">
            {row?.creationDate?.substring(0, 10) || "—"}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ConfirmationPopup = ({ show, onConfirm, onCancel }) => {
  if (!show) return null;

  return (
    <div style={styles.popupOverlay}>
      <div style={styles.popupContent}>
        <h3 style={styles.popupTitle}>
          Are you sure you want to change status to Completed?
        </h3>
        <div style={styles.popupButtons}>
          <button onClick={onCancel} style={styles.cancelButton}>
            Cancel
          </button>
          <button onClick={onConfirm} style={styles.confirmButton}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationMessage = ({ message, type = 'success' }) => {
  if (!message) return null;

  return (
    <div
      className="popup-message"
      style={type === 'error' ? { backgroundColor: 'red' } : {}}
    >
      {message}
    </div>
  );
};

const Stamp = () => {
  const fileInputRefs = useRef({});

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [requestID, setRequestID] = useState(null);
  const [openRequests, setOpenRequests] = useState({});
  const [currentOpenRequestId, setCurrentOpenRequestId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadingRows, setUploadingRows] = useState({});
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState("");
  const [open, setOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [uploadMessage, setUploadMessage] = useState(null);

  const filterByClient = (clientData) => {
    if (!clientData || clientData === "") {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter((task) => {
      const taskClientName = task.clientName?.toLowerCase().trim();
      const selectedClientName = `${clientData.firstName} ${clientData.lastName}`
        .toLowerCase()
        .trim();
      return taskClientName === selectedClientName;
    });

    setFilteredData(filtered);
  };

  // API Calls
  const fetchStampingData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/translate/requests/stamping`, {
        headers: getAuthHeaders()
      });
      setData(res.data.data);
    } catch (error) {
      console.error('Error fetching stamping data:', error);
    }
  };

  const fetchAllClients = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/users?role=CLIENT`, {
        headers: getAuthHeaders()
      });
      setClients(res.data.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchDocumentsByRequestId = async (id, shouldToggle = true) => {
    setRequestID(id);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/translate/docs?requestId=${id}`,
        { headers: getAuthHeaders() }
      );

      const translateDocs = res.data.data.map((doc) => ({
        id: doc.id,
        document: doc.documentUrl,
        documentId: doc.documentId,
        templateId: doc.templateId,
        templateUrl: doc.templateUrl,
        translatedDocumentId: doc.translatedDocumentId,
        translatedDocumentUrl: doc.translatedDocumentUrl,
        finalTranslatedDocumentId: doc.finalTranslatedDocumentId,
        finalTranslatedDocumentUrl: doc.finalTranslatedDocumentUrl,
        creationDate: doc.creationDate,
      }));

      // Update the documents data
      setOpenRequests((prev) => ({
        ...prev,
        [id]: translateDocs,
      }));

      // Only toggle open/close if shouldToggle is true
      if (shouldToggle) {
        setCurrentOpenRequestId((prevId) => 
          prevId === id ? null : id
        );
      }
    } catch (err) {
      console.error('Error fetching documents by request ID:', err);
    }
  };

  const updateRequestStatus = async () => {
    try {
      await axios.patch(
        `${API_BASE_URL}/translate/requests/${requestID}/status?status=COMPLETED`,
        {},
        { headers: getAuthHeaders() }
      );

      showNotification('Status changed successfully to COMPLETED', 'success');
      setShowPopup(false);
      await fetchStampingData();
    } catch (error) {
      const errorMsg = error.response?.data?.data || 'Failed to update status';
      showNotification(errorMsg, 'error');
      setShowPopup(false);
    }
  };

  const handleDownload = async (mediaId, name) => {
    try {

      console.log('name ', name);
      const fileName = getFileName(name);

      console.log('file name ', fileName);
      const url = `${API_BASE_URL}/media/download/${mediaId}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      const blob = response.data;
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      const contentDisposition = response.headers["content-disposition"];
      let downloadFileName = fileName;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match?.[1]) {
          downloadFileName = match[1].replace(/['"]/g, "");
        }
      }

      if (!downloadFileName.includes(".")) {
        const contentType = response.headers["content-type"];
        const ext = contentType?.split("/")[1] || "bin";
        downloadFileName = `${downloadFileName}.${ext}`;
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      showNotification('Download failed', 'error');
    }
  };

  const handleIconClick = (rowId) => {
    fileInputRefs.current[rowId]?.click();
  };

  const handleFileChange = async (event, rowId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingRows(prev => ({ ...prev, [rowId]: true }));
      setSelectedFiles(prev => ({ ...prev, [rowId]: file }));

      const formData = new FormData();
      formData.append("file", file);

      await axios.patch(
        `${API_BASE_URL}/translate/docs/${rowId}/final-translated-document`,
        formData,
        { headers: getAuthHeaders() }
      );

      showNotification('Document uploaded successfully', 'upload');
      // Pass false to update without closing
      await fetchDocumentsByRequestId(currentOpenRequestId, false);
    } catch (err) {
      const errorMsg = err?.response?.data?.data || err.message;
      console.error("Error uploading document:", errorMsg);
      showNotification('Upload failed', 'error');
    } finally {
      setUploadingRows(prev => ({ ...prev, [rowId]: false }));
    }
  };

  const handleDeleteClick = async (id) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/translate/docs/${id}/final-translated-document`,
        { headers: getAuthHeaders() }
      );

      await fetchStampingData();
      // Pass false to update without closing
      await fetchDocumentsByRequestId(currentOpenRequestId, false);
      showNotification('Document deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting document:', error?.response?.data?.data);
      showNotification('Delete failed', 'error');
    }
  };

  const handleNextClick = () => {
    setShowPopup(true);
  };

  const handleCancel = () => {
    setShowPopup(false);
  };

  const showNotification = (message, type) => {
    if (type === 'success') {
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 3000);
    } else if (type === 'error') {
      setErrorMessage(message);
      setTimeout(() => setErrorMessage(null), 3000);
    } else if (type === 'upload') {
      setUploadMessage(message);
      setTimeout(() => setUploadMessage(null), 3000);
    }
  };

  useEffect(() => {
    fetchStampingData();
    fetchAllClients();
  }, []);

  useEffect(() => {
    filterByClient(selected);
  }, [selected, data]);

  return (
    <>
      <div className='Stamp-container'>
        <h2 className="requests-title">Stamping Management</h2>

        <ClientDropdown
          clients={clients}
          selected={selected}
          setSelected={setSelected}
          open={open}
          setOpen={setOpen}
        />

        <div>
          {filteredData.length === 0 ? (
            <div style={styles.emptyState}>
              No stamping requests found.
            </div>
          ) : (
            filteredData.map((task) => (
              <div key={task.id} style={{ marginBottom: "20px" }}>
                <div
                  onClick={() => fetchDocumentsByRequestId(task.id, true)}
                  className="task-row"
                  style={styles.taskRow}
                >
                  <TaskField label="Client" value={task.clientName} />
                  <TaskField label="Request Date" value={formatDate(task.requestDate)} />
                  <TaskField label="Task Title" value={task.taskTitle} />
                  <TaskField label="Total Pages" value={task.totalPages} />
                  <TaskField label="Translator" value={task.translatorName} />
                  <TaskField label="Proofreader" value={task.proofreaderName} />
                  <TaskField label="Delivery Date" value={formatDate(task.deliveryDate)} />
                  <div style={styles.taskField}>
                    <strong style={styles.fieldLabel}>Status</strong>
                    <br />
                    <span style={styles.statusBadge}>
                      {task.status || "Unknown"}
                    </span>
                  </div>
                </div>

                {currentOpenRequestId === task.id && openRequests[task.id] && (
                  <>
                    <DocumentTable
                      documents={openRequests[task.id]}
                      handleDownload={handleDownload}
                      handleIconClick={handleIconClick}
                      handleFileChange={handleFileChange}
                      handleDeleteClick={handleDeleteClick}
                      fileInputRefs={fileInputRefs}
                      uploadingRows={uploadingRows}
                      selectedFiles={selectedFiles}
                    />
                    <div style={{ display: "flex", justifyContent: "end" }}>
                      <button onClick={handleNextClick} style={styles.nextButton}>
                        NEXT
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <NotificationMessage message={errorMessage} type="error" />
      <NotificationMessage message={successMessage} type="success" />
      <NotificationMessage message={uploadMessage} type="success" />

      <ConfirmationPopup
        show={showPopup}
        onConfirm={updateRequestStatus}
        onCancel={handleCancel}
      />
    </>
  );
};

export default Stamp;